import type {
  RolePlayAssessment,
  RolePlayConfig,
  RolePlayScenario,
} from "@/lib/powtoon-be/types";

function randomId(prefix: string) {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rand}`;
}

const newScenarioId = () => randomId("rps");
export const newMetricId = () => randomId("rpm");

export const MAX_TURNS_MIN = 3;
export const MAX_TURNS_MAX = 15;
export const DEFAULT_MAX_TURNS = 8;

/** Integer 3–15, else undefined (never writes a default into the record). */
export function validMaxTurns(v: unknown): number | undefined {
  const n = typeof v === "string" && v.trim() ? Number(v) : v;
  if (typeof n !== "number" || !Number.isFinite(n)) return undefined;
  const r = Math.round(n);
  return r >= MAX_TURNS_MIN && r <= MAX_TURNS_MAX ? r : undefined;
}

function emptyAssessment(): RolePlayAssessment {
  return {
    competency_metrics: [],
    success_triggers: [],
    red_flags: [],
    termination_conditions: [],
  };
}

export function emptyScenario(): RolePlayScenario {
  return {
    id: newScenarioId(),
    avatar_role: "",
    avatar_personality: "",
    emotional_state: "",
    hidden_information: "",
    learner_role: "",
    emotional_progression: "",
    framing_script: "",
    assessment: emptyAssessment(),
  };
}

export function defaultRolePlayConfig(): RolePlayConfig {
  // Exactly one scenario: the Powtoon BE grades by inner scenario id, so the
  // stored shape keeps the array while the editor exposes a single entry.
  return { version: 1, goal: "", scenarios: [emptyScenario()] };
}

/**
 * The single role-play scenario the editor edits. Keeps the existing id when
 * one is present — regenerating it would orphan past sessions/transcripts.
 */
export function singleScenarioOf(cfg: RolePlayConfig): RolePlayScenario {
  return cfg.scenarios[0] ?? emptyScenario();
}

/** True when the trainer hasn't authored anything worth persisting yet. */
export function isRolePlayConfigEmpty(cfg: RolePlayConfig | null | undefined): boolean {
  if (!cfg) return true;
  const s = cfg.scenarios[0];
  if (!s) return true;
  const filled = [
    cfg.goal,
    s.avatar_role,
    s.learner_role,
    s.avatar_personality,
    s.emotional_state,
    s.hidden_information,
    s.emotional_progression,
  ].some((v) => (v ?? "").trim().length > 0);
  const hasCriteria =
    s.assessment.competency_metrics.some((m) => m.name.trim().length > 0) ||
    s.assessment.termination_conditions.some((t) => t.trim().length > 0);
  return !filled && !hasCriteria;
}

/** Coerce server/legacy values into the canonical shape. */
export function normalizeScenario(raw: unknown): RolePlayScenario {
  const r = (raw ?? {}) as Partial<RolePlayScenario> & {
    assessment?: Partial<RolePlayAssessment> & Record<string, unknown>;
  };
  const asArr = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  const a = (r.assessment ?? {}) as Partial<RolePlayAssessment>;
  const metrics = Array.isArray(a.competency_metrics) ? a.competency_metrics : [];
  return {
    id: typeof r.id === "string" && r.id ? r.id : newScenarioId(),
    avatar_role: r.avatar_role ?? "",
    avatar_personality: r.avatar_personality ?? "",
    emotional_state: r.emotional_state ?? "",
    hidden_information: r.hidden_information ?? "",
    learner_role: r.learner_role ?? "",
    emotional_progression: r.emotional_progression ?? "",
    framing_script: r.framing_script ?? "",
    assessment: {
      competency_metrics: metrics.map((m) => ({
        id: typeof m?.id === "string" && m.id ? m.id : newMetricId(),
        name: typeof m?.name === "string" ? m.name : "",
        description: typeof m?.description === "string" ? m.description : "",
      })),
      success_triggers: asArr(a.success_triggers),
      red_flags: asArr(a.red_flags),
      termination_conditions: asArr(a.termination_conditions),
      ...(validMaxTurns(a.max_turns) !== undefined
        ? { max_turns: validMaxTurns(a.max_turns) }
        : {}),
    },
  };
}

export function normalizeRolePlayConfig(
  raw: RolePlayConfig | undefined | null,
): RolePlayConfig {
  if (!raw) return defaultRolePlayConfig();
  // Only the first scenario is ever played or scored — keep it, drop the rest.
  const first = Array.isArray(raw.scenarios) && raw.scenarios.length
    ? normalizeScenario(raw.scenarios[0])
    : emptyScenario();
  return {
    version: 1,
    goal: typeof raw.goal === "string" ? raw.goal : "",
    scenarios: [first],
  };
}